import { useCallback, useEffect, useRef, useState } from "react";
import type { Account, Candlestick, Deposit, Market, Order, OrderbookDepth, TpsStats, TradingPair } from "@/lib/wsTypes";
import { parseAccount, parseCandlestick, parseDeposit, parseMarket, parseOrder, parseTpsStats } from "@/lib/wsTypes";

/**
 * Live market-data client for the channel streaming API (see
 * docs/streaming-api.md). Connects to `/ws`, reads the plaintext `welcome`
 * (no handshake, no app-layer encryption -- TLS covers that in prod), then
 * subscribes to channels: public `markets` + `stats`, and per-market
 * `orderbook:<m>` + `candles:<m>:<interval>`, plus private (authed) `account`
 * + `orders` + `deposits`. The server replies with a `snapshot` per channel,
 * then `update` deltas. Order/deposit submission still goes over the encrypted
 * HTTP path -- this socket is read-only.
 */

/** Candle granularities the UI renders (chart timeframes + 24h stats). */
const CANDLE_GRANULARITIES = ["1s", "1m", "1h", "1d"] as const;

/** Placeholder pair for a depth row before its market's metadata has arrived;
 * depth consumers key by `pairKey` and read price/amounts, never `pair`. */
const UNKNOWN_PAIR: TradingPair = { base: { tag: "Ada" }, quote: { tag: "Ada" } };

function initialLedgerView() {
  return {
    account: null as Account | null,
    deposits: {} as Record<string, Deposit>,
    markets: {} as Record<string, Market>,
    depths: {} as Record<string, OrderbookDepth[]>,
    orders: {} as Record<string, Order>,
    candlesticks: {} as Record<string, Candlestick>,
    tpsStats: null as TpsStats | null,
    tradingEnabled: false,
  };
}

type LedgerView = ReturnType<typeof initialLedgerView>;
/** market (canonical key) -> price string -> aggregated level. */
type Book = Map<string, Map<string, OrderbookDepth>>;

/** The per-market channels a viewer needs for the market it's looking at. */
function marketChannels(marketKey: string): string[] {
  return [`orderbook:${marketKey}`, ...CANDLE_GRANULARITIES.map((g) => `candles:${marketKey}:${g}`)];
}

export function useWebSocket(url: string, accountHash: string | null) {
  const ws = useRef<WebSocket | null>(null);
  // The single market this client currently watches (orderbook + candles). Only
  // this market is subscribed -- not all of them -- so a page load costs ~7
  // snapshots, not ~27, which keeps a connect storm cheap. Survives reconnects.
  const watchedMarket = useRef<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ledgerView, setLedgerView] = useState(initialLedgerView);

  useEffect(() => {
    let stopped = false;
    // A half-open socket (TCP alive but no data -- server restarted mid-stream,
    // NAT timeout, a stale server-side view) never fires onclose, so the
    // reconnect path never runs and the UI freezes on its last snapshot. Track
    // the last frame time and force a reconnect if the feed goes quiet; the
    // `stats` heartbeat streams ~1/s, so genuine silence this long means a dead
    // feed.
    let lastFrameAt = Date.now();

    function connect() {
      const socket = new WebSocket(url);
      socket.binaryType = "arraybuffer";
      ws.current = socket;

      let welcomed = false;
      // The aggregated book that backs `depths`, rebuilt fresh on each connect
      // (no cross-reconnect phantoms).
      const book: Book = new Map();

      const auth = () => ({ account: accountHash, nonce: Date.now(), signature: "" });
      const sub = (channels: string[], withAuth = false) => {
        if (socket.readyState !== WebSocket.OPEN) return;
        socket.send(JSON.stringify(withAuth ? { op: "subscribe", channels, auth: auth() } : { op: "subscribe", channels }));
      };

      socket.onopen = () => {
        setConnected(true);
        setError(null);
        lastFrameAt = Date.now();
      };
      socket.onclose = () => {
        setConnected(false);
        if (!stopped) setTimeout(connect, 3_000);
      };
      socket.onerror = (e) => {
        console.error("[ws] error", e);
        setError("WebSocket connection failed.");
      };

      socket.onmessage = (e) => {
        lastFrameAt = Date.now();
        const text = typeof e.data === "string" ? e.data : new TextDecoder().decode(e.data as ArrayBuffer);
        let parsed: any;
        try {
          parsed = JSON.parse(text);
        } catch {
          return;
        }

        // A batch of live updates arrives as a JSON array of data messages; a
        // snapshot or control frame arrives as a single object.
        if (Array.isArray(parsed)) {
          applyDataMessages(parsed);
          return;
        }
        if (parsed && typeof parsed.event === "string") {
          if (parsed.event === "welcome" && !welcomed) {
            welcomed = true;
            // The server replays a full snapshot per channel on (re)subscribe,
            // and a snapshot only re-inserts what is currently live -- it never
            // deletes. Drop the volatile collections here so a row deleted on
            // the ledger during a disconnect gap can't linger as a phantom.
            // Keep `markets` (re-sent on the markets snapshot; also stops
            // `selectedMarket` flickering null on every reconnect).
            book.clear();
            setLedgerView((prev) => ({
              ...initialLedgerView(),
              markets: prev.markets,
            }));
            sub(["markets", "stats"]);
            sub(["account", "orders", "deposits"], true);
            // Re-subscribe the market we're watching (if any) on this fresh socket.
            if (watchedMarket.current) sub(marketChannels(watchedMarket.current));
          } else if (parsed.event === "error") {
            console.error("[ws] server error", parsed);
          }
          return;
        }
        if (parsed && typeof parsed.channel === "string") {
          applyDataMessages([parsed]);
        }
      };

      /** Fold a batch of `{channel,type,data}` messages into one state update. */
      function applyDataMessages(messages: any[]) {
        setLedgerView((prev) => {
          const next: LedgerView = {
            ...prev,
            deposits: prev.deposits,
            markets: prev.markets,
            depths: prev.depths,
            orders: prev.orders,
            candlesticks: prev.candlesticks,
          };
          const dirtyBooks = new Set<string>();

          for (const msg of messages) {
            if (!msg || typeof msg.channel !== "string") continue;
            const { channel, type, data } = msg;

            if (channel === "markets") {
              next.markets = { ...next.markets };
              for (const raw of data ?? []) {
                const m = parseMarket(raw);
                next.markets[m.key] = m;
              }
            } else if (channel === "stats") {
              if (data) {
                next.tradingEnabled = !!data.tradingEnabled;
                next.tpsStats = data.tps ? parseTpsStats(data.tps) : next.tpsStats;
              }
            } else if (channel === "account") {
              if (data) next.account = parseAccount(data);
            } else if (channel === "orders") {
              next.orders = { ...next.orders };
              const list = type === "snapshot" ? (data ?? []) : [data];
              for (const raw of list) {
                if (!raw) continue;
                const o = parseOrder(raw);
                next.orders[o.id.toString()] = o;
              }
            } else if (channel === "deposits") {
              next.deposits = { ...next.deposits };
              const list = type === "snapshot" ? (data ?? []) : [data];
              for (const raw of list) {
                if (!raw) continue;
                const d = parseDeposit(raw);
                next.deposits[d.requestId.toString()] = d;
              }
            } else if (channel.startsWith("candles:")) {
              next.candlesticks = { ...next.candlesticks };
              const list = type === "snapshot" ? (data ?? []) : [data];
              for (const raw of list) {
                if (!raw) continue;
                const c = parseCandlestick(raw);
                next.candlesticks[c.key] = c;
              }
            } else if (channel.startsWith("orderbook:")) {
              applyOrderbook(book, data, type);
              if (data?.market) dirtyBooks.add(data.market);
            }
          }

          // Rebuild the affected markets' depth arrays from the aggregated book.
          if (dirtyBooks.size > 0) {
            next.depths = { ...next.depths };
            for (const market of dirtyBooks) {
              const levels = book.get(market);
              const pair = next.markets[market]?.tradingPair ?? UNKNOWN_PAIR;
              next.depths[market] = levels
                ? [...levels.values()].map((l) => ({ ...l, pair }))
                : [];
            }
          }
          return next;
        });
      }
    }

    connect();

    const watchdog = setInterval(() => {
      if (stopped) return;
      if (Date.now() - lastFrameAt > 15_000 && ws.current) {
        console.warn("[ws] no frames for >15s -- forcing reconnect");
        lastFrameAt = Date.now(); // avoid re-firing during the reconnect gap
        try {
          ws.current.close();
        } catch {
          /* onclose drives the reconnect */
        }
      }
    }, 5_000);

    return () => {
      stopped = true;
      clearInterval(watchdog);
      ws.current?.close();
    };
  }, [url, accountHash]);

  const send = useCallback((msg: string) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(msg);
    }
  }, []);

  /** Point this client's orderbook + candle feed at `marketKey` (the market the
   * user is viewing). Unsubscribes the previous market and subscribes the new
   * one; idempotent; re-applied automatically across reconnects. */
  const watchMarket = useCallback((marketKey: string | null) => {
    const prev = watchedMarket.current;
    if (prev === marketKey) return;
    watchedMarket.current = marketKey;
    const sock = ws.current;
    if (!sock || sock.readyState !== WebSocket.OPEN) return;
    if (prev) sock.send(JSON.stringify({ op: "unsubscribe", channels: marketChannels(prev) }));
    if (marketKey) sock.send(JSON.stringify({ op: "subscribe", channels: marketChannels(marketKey) }));
  }, []);

  return { ledgerView, connected, error, send, watchMarket };
}

/** Apply an orderbook `snapshot` (full book, reset first) or `update` (one price
 * level; a `0` bid and ask means the level was removed) into `book`. Each depth
 * row carries both sides at its price. */
function applyOrderbook(book: Book, data: any, type: string): void {
  const market: string | undefined = data?.market;
  if (!market) return;
  let levels = book.get(market);
  if (type === "snapshot" || !levels) {
    levels = new Map();
    book.set(market, levels);
  }

  const at = (priceStr: string): OrderbookDepth => {
    let lvl = levels!.get(priceStr);
    if (!lvl) {
      lvl = {
        key: priceStr,
        pairKey: market,
        pair: UNKNOWN_PAIR,
        price: BigInt(priceStr),
        bidAmount: 0n,
        askAmount: 0n,
      };
      levels!.set(priceStr, lvl);
    }
    return lvl;
  };

  for (const [priceStr, qtyStr] of data.bids ?? []) at(priceStr).bidAmount = BigInt(qtyStr);
  for (const [priceStr, qtyStr] of data.asks ?? []) at(priceStr).askAmount = BigInt(qtyStr);
  // Drop levels emptied on both sides.
  for (const [priceStr, lvl] of levels) {
    if (lvl.bidAmount === 0n && lvl.askAmount === 0n) levels.delete(priceStr);
  }
}
